import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Sign In — Protovera';
    return () => { document.title = 'Protovera — AI Research Protocol Generator for Life Sciences'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const submittedEmail = String(formData.get('email') || '').trim();
    const submittedPassword = String(formData.get('password') || '');

    setEmail(submittedEmail);
    setPassword(submittedPassword);
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/auth/login', {
        email: submittedEmail,
        password: submittedPassword,
      });
      login(res.data.token, res.data.user);
      navigate('/app');
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-semibold text-[#171717]"
            style={{ letterSpacing: '-1.28px' }}
          >
            Protovera
          </h1>
          <p className="text-[#808080] mt-2 text-sm">Sign in to Protovera</p>
        </div>

        <div
          className="bg-white rounded-xl p-8"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px, #fafafa 0px 0px 0px 1px inset' }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                className="block text-sm font-medium text-[#171717] mb-1"
                style={{ letterSpacing: '-0.32px' }}
              >
                Email
              </label>
              <input
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
            </div>

            <div>
              <label
                className="block text-sm font-medium text-[#171717] mb-1"
                style={{ letterSpacing: '-0.32px' }}
              >
                Password
              </label>
              <input
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="text-right">
              <Link to="/forgot-password" className="text-xs text-[#4d4d4d] hover:text-[#171717] hover:underline">Forgot password?</Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#171717] text-white rounded-md font-medium text-sm hover:bg-[#2e2e2e] disabled:opacity-50 transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-sm text-[#808080] mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#171717] font-medium hover:underline">Sign up free</Link>
          </p>
        </div>

        <p className="text-center text-sm text-[#808080] mt-4">
          <Link to="/pricing" className="hover:underline hover:text-[#4d4d4d]">View pricing plans</Link>
        </p>
      </div>
    </div>
  );
}
